/**
 * Chunk planning for browser-side transcription: slice a mono PCM buffer
 * into fixed-duration windows, each transcribed as its own request and
 * merged back with its time offset.
 */

export interface AudioChunkPlan {
  startSample: number;
  sampleCount: number;
  /** Seconds from the start of the recording — added to segment timestamps. */
  offsetSec: number;
}

export const DEFAULT_CHUNK_SECONDS = 600; // 10 min ≈ 19.2 MB WAV @16k mono

/** How far a boundary may move to land in silence. */
const BOUNDARY_SEARCH_SECONDS = 15;

/** Energy window for the silence scan — roughly an inter-phrase pause. */
const QUIET_WINDOW_SECONDS = 0.02;

/**
 * Floor for a refined chunk's duration. The API rejects audio shorter
 * than 0.1 s, and a rejected final chunk fails the whole job after every
 * earlier chunk was already transcribed and billed.
 * @internal Exported for tests.
 */
export const MIN_CHUNK_SECONDS = 1;

export function planChunks(
  totalSamples: number,
  sampleRate: number,
  chunkSeconds: number = DEFAULT_CHUNK_SECONDS,
): AudioChunkPlan[] {
  if (totalSamples <= 0) return [];
  const samplesPerChunk = Math.max(1, Math.floor(chunkSeconds * sampleRate));
  const plans: AudioChunkPlan[] = [];
  for (let start = 0; start < totalSamples; start += samplesPerChunk) {
    plans.push({
      startSample: start,
      sampleCount: Math.min(samplesPerChunk, totalSamples - start),
      offsetSec: start / sampleRate,
    });
  }
  // Fold a sub-minimum remainder into the previous chunk — the boundary
  // search below cannot rescue a tail its radius can't reach.
  const tail = plans[plans.length - 1];
  if (plans.length > 1 && tail.sampleCount < Math.floor(MIN_CHUNK_SECONDS * sampleRate)) {
    plans.pop();
    plans[plans.length - 1].sampleCount += tail.sampleCount;
  }
  return plans;
}

/**
 * Move each interior chunk boundary to the quietest point within ±15 s of
 * its planned position, so chunks cut in pauses instead of mid-word — the
 * main transcription-quality risk of fixed-duration windows. Chunks stay
 * contiguous; every sample remains covered exactly once, and a cut never
 * lands within MIN_CHUNK_SECONDS of the previous cut or the buffer end
 * (a degenerate search window keeps the planned cut instead, so a chunk
 * is never shorter than the plan's own).
 */
export function refineChunkBoundaries(
  plans: AudioChunkPlan[],
  samples: Float32Array,
  sampleRate: number,
  searchSeconds: number = BOUNDARY_SEARCH_SECONDS,
): AudioChunkPlan[] {
  if (plans.length < 2) return plans;

  const last = plans[plans.length - 1];
  const totalSamples = last.startSample + last.sampleCount;
  const radius = Math.floor(searchSeconds * sampleRate);
  const window = Math.max(1, Math.floor(QUIET_WINDOW_SECONDS * sampleRate));
  const minChunk = Math.max(window, Math.floor(MIN_CHUNK_SECONDS * sampleRate));

  const boundaries: number[] = [0];
  for (let i = 1; i < plans.length; i++) {
    const nominal = plans[i].startSample;
    // The previous (possibly moved) boundary and the buffer end fence the
    // search so boundaries stay strictly increasing and a cut never lands
    // within minChunk of either — a fade-out tail would otherwise pull the
    // final cut to within one energy window of the buffer end.
    const lo = Math.max(boundaries[i - 1] + minChunk, nominal - radius);
    const hi = Math.min(totalSamples - minChunk, nominal + radius);
    boundaries.push(hi > lo ? quietestCenter(samples, lo, hi, window, nominal) : nominal);
  }

  return boundaries.map((startSample, i) => {
    const end = i + 1 < boundaries.length ? boundaries[i + 1] : totalSamples;
    return {
      startSample,
      sampleCount: end - startSample,
      offsetSec: startSample / sampleRate,
    };
  });
}

/**
 * Center of the minimum-energy window in [lo, hi), via a sliding sum.
 * Energy ties (uniform audio, long silences) resolve toward the planned
 * position so boundaries don't drift for no acoustic reason.
 */
function quietestCenter(
  samples: Float32Array,
  lo: number,
  hi: number,
  window: number,
  nominal: number,
): number {
  const half = window >> 1;
  let sum = 0;
  for (let i = lo; i < lo + window; i++) {
    sum += samples[i] * samples[i];
  }
  let bestSum = sum;
  let bestStart = lo;
  let bestDistance = Math.abs(lo + half - nominal);
  for (let start = lo + 1; start + window <= hi; start++) {
    const dropped = samples[start - 1];
    const added = samples[start + window - 1];
    sum += added * added - dropped * dropped;
    const distance = Math.abs(start + half - nominal);
    if (sum < bestSum || (sum === bestSum && distance < bestDistance)) {
      bestSum = sum;
      bestStart = start;
      bestDistance = distance;
    }
  }
  return bestStart + half;
}
